import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { supabase } from '@/lib/supabase'
import type { Constitution, ConstitutionVersion } from '@/types'

// --- Collapsible sections logic (shared with ConstitutionPage) ---
const COLLAPSED_KEY = 'tradingApp_constitutionCollapsed'

function loadCollapsedState(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY)
    if (raw) return new Set(JSON.parse(raw))
  } catch { /* ignore */ }
  return new Set()
}

function saveCollapsedState(collapsed: Set<string>) {
  localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed]))
}

interface MarkdownSection {
  id: string
  level: number
  heading: string
  ownContent: string
  children: MarkdownSection[]
}

function parseMarkdownSections(content: string): MarkdownSection[] {
  const lines = content.split('\n')
  const root: MarkdownSection[] = []
  const stack: { level: number; section: MarkdownSection }[] = []

  let currentLines: string[] = []
  let idCounter = 0

  const flush = () => {
    const text = currentLines.join('\n')
    currentLines = []
    return text
  }

  const makeId = (heading: string) => {
    idCounter++
    return `s-${idCounter}-${heading.slice(0, 40).replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/)

    if (headingMatch) {
      const ownText = flush()
      if (stack.length > 0) {
        stack[stack.length - 1].section.ownContent += ownText
      } else if (root.length > 0) {
        root[root.length - 1].ownContent += ownText
      } else if (ownText.trim()) {
        root.push({ id: makeId('preamble'), level: 0, heading: '', ownContent: ownText, children: [] })
      }

      const level = headingMatch[1].length
      const heading = headingMatch[2].trim()
      const newSection: MarkdownSection = { id: makeId(heading), level, heading, ownContent: '\n', children: [] }

      while (stack.length > 0 && stack[stack.length - 1].level >= level) {
        stack.pop()
      }

      if (stack.length === 0) {
        root.push(newSection)
      } else {
        stack[stack.length - 1].section.children.push(newSection)
      }

      stack.push({ level, section: newSection })
    } else {
      currentLines.push(line)
    }
  }

  const remaining = flush()
  if (stack.length > 0) {
    stack[stack.length - 1].section.ownContent += remaining
  } else if (root.length > 0) {
    root[root.length - 1].ownContent += remaining
  } else if (remaining.trim()) {
    root.push({ id: `s-${++idCounter}-tail`, level: 0, heading: '', ownContent: remaining, children: [] })
  }

  return root
}

// Flatten sections back to markdown for a given section (heading + own content + all children recursively)
function sectionToMarkdown(section: MarkdownSection): string {
  let md = ''
  if (section.heading) {
    md += `${'#'.repeat(section.level)} ${section.heading}\n`
  }
  md += section.ownContent
  for (const child of section.children) {
    md += sectionToMarkdown(child)
  }
  return md
}

function sectionsToMarkdown(sections: MarkdownSection[]): string {
  return sections.map(s => sectionToMarkdown(s)).join('')
}

// Old splitIntoSections/mergeSections replaced by parseMarkdownSections above

function EditableCollapsibleSection({ section, collapsed, toggleCollapse, editingSectionId, editContent, setEditContent, onSectionClick, onSave, onCancel, saving, saveError, depth = 0 }: {
  section: MarkdownSection
  collapsed: Set<string>
  toggleCollapse: (id: string) => void
  editingSectionId: string | null
  editContent: string
  setEditContent: (v: string) => void
  onSectionClick: (id: string) => void
  onSave: () => void
  onCancel: () => void
  saving: boolean
  saveError: string | null
  depth?: number
}) {
  const isCollapsed = collapsed.has(section.id)
  const isEditing = editingSectionId === section.id

  if (!section.heading) {
    // Preamble content — click to edit
    return (
      <div className="report-content cursor-pointer hover:bg-blue-50 rounded transition-colors" onClick={() => onSectionClick(section.id)}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.ownContent}</ReactMarkdown>
      </div>
    )
  }

  if (isEditing) {
    return (
      <div className="border-2 border-blue-500 rounded-lg p-4 bg-white mb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-blue-600">Editing: {section.heading}</span>
          <div className="flex gap-2">
            <button onClick={onCancel} disabled={saving}
              className="px-3 py-1.5 text-sm rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50">Cancel</button>
            <button onClick={onSave} disabled={saving}
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Section'}
            </button>
          </div>
        </div>
        <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)}
          className="w-full p-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 font-mono text-sm resize-y focus:outline-none focus:border-blue-500"
          style={{ minHeight: '200px', height: `${Math.max(200, editContent.split('\n').length * 22)}px` }}
          spellCheck={false} />
        {saveError && <div className="mt-2 text-sm text-red-400">{saveError}</div>}
      </div>
    )
  }

  const chevron = isCollapsed ? '▶' : '▼'

  return (
    <div>
      {/* Heading row: chevron toggles collapse, clicking text edits */}
      <div className="flex items-center gap-2 group">
        <span
          className="text-gray-400 text-xs w-4 shrink-0 cursor-pointer hover:text-gray-600"
          onClick={() => toggleCollapse(section.id)}
        >
          {chevron}
        </span>
        <div
          className="report-content flex-1 cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 transition-colors"
          onClick={() => onSectionClick(section.id)}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{`${'#'.repeat(section.level)} ${section.heading}`}</ReactMarkdown>
        </div>
        <span className="opacity-0 group-hover:opacity-100 text-xs text-gray-400 transition-opacity shrink-0">✏️</span>
      </div>

      {/* Content (hidden when collapsed) */}
      {!isCollapsed && (
        <div className={`${section.level >= 2 ? 'ml-4 border-l border-gray-200 pl-3' : ''}`}>
          {section.ownContent.trim() && (
            <div className="report-content cursor-pointer hover:bg-blue-50 rounded transition-colors" onClick={() => onSectionClick(section.id)}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.ownContent}</ReactMarkdown>
            </div>
          )}
          {section.children.map(child => (
            <EditableCollapsibleSection
              key={child.id}
              section={child}
              collapsed={collapsed}
              toggleCollapse={toggleCollapse}
              editingSectionId={editingSectionId}
              editContent={editContent}
              setEditContent={setEditContent}
              onSectionClick={onSectionClick}
              onSave={onSave}
              onCancel={onCancel}
              saving={saving}
              saveError={saveError}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function ConstitutionEditPage() {
  const [constitution, setConstitution] = useState<Constitution | null>(null)
  const [versions, setVersions] = useState<ConstitutionVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeResult, setActiveResult] = useState(0)
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsedState())
  const contentRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const toggleCollapse = useCallback((id: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      saveCollapsedState(next)
      return next
    })
  }, [])

  const collapseAll = useCallback(() => {
    if (!constitution?.content) return
    const sections = parseMarkdownSections(constitution.content)
    const allIds = new Set<string>()
    const collectIds = (secs: MarkdownSection[]) => {
      for (const s of secs) {
        if (s.heading) allIds.add(s.id)
        collectIds(s.children)
      }
    }
    collectIds(sections)
    setCollapsed(allIds)
    saveCollapsedState(allIds)
  }, [constitution])

  const expandAll = useCallback(() => {
    setCollapsed(new Set())
    saveCollapsedState(new Set())
  }, [])

  const fetchConstitution = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('constitution')
        .select('*')
        .eq('is_active', true)
        .single()
      if (err) throw new Error(err.message)
      setConstitution(data as unknown as Constitution)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load constitution')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchVersions = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('constitution_versions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10)
      if (err) return
      setVersions((data || []).map((v: any) => ({
        id: v.id,
        constitutionId: v.constitution_id,
        content: v.content,
        changeSource: v.change_source,
        createdAt: v.created_at,
      })))
    } catch { /* ignore */ }
  }, [])

  useEffect(() => { fetchConstitution(); fetchVersions() }, [fetchConstitution, fetchVersions])
  useEffect(() => { if (saveSuccess) { const t = setTimeout(() => setSaveSuccess(false), 3000); return () => clearTimeout(t) } }, [saveSuccess])

  const sections = constitution ? parseMarkdownSections(constitution.content) : []

  // Find a section by id recursively
  const findSectionById = (secs: MarkdownSection[], id: string): MarkdownSection | null => {
    for (const s of secs) {
      if (s.id === id) return s
      const found = findSectionById(s.children, id)
      if (found) return found
    }
    return null
  }

  const handleSectionClick = (sectionId: string) => {
    const section = findSectionById(sections, sectionId)
    if (!section) return
    setEditingSectionId(sectionId)
    setEditContent(sectionToMarkdown(section))
    setSaveError(null)
  }

  const handleCancel = () => {
    setEditingSectionId(null)
    setEditContent('')
    setSaveError(null)
  }

  const handleSave = async () => {
    if (!constitution || !editingSectionId) return
    setSaving(true)
    setSaveError(null)

    try {
      // Replace the section content in the full document
      // Strategy: rebuild full document, replacing the target section's markdown
      const targetSection = findSectionById(sections, editingSectionId)
      if (!targetSection) throw new Error('Section not found')

      const originalMd = sectionToMarkdown(targetSection)
      const fullContent = constitution.content
      const idx = fullContent.indexOf(originalMd.trimEnd())

      let newContent: string
      if (idx >= 0) {
        newContent = fullContent.slice(0, idx) + editContent + fullContent.slice(idx + originalMd.trimEnd().length)
      } else {
        // Fallback: replace by heading match
        const headingLine = `${'#'.repeat(targetSection.level)} ${targetSection.heading}`
        const headingIdx = fullContent.indexOf(headingLine)
        if (headingIdx >= 0) {
          // Find next heading at same or higher level
          const afterHeading = fullContent.slice(headingIdx + headingLine.length)
          const nextHeadingMatch = afterHeading.match(new RegExp(`^#{1,${targetSection.level}}\\s`, 'm'))
          const endIdx = nextHeadingMatch ? headingIdx + headingLine.length + (afterHeading.indexOf(nextHeadingMatch[0])) : fullContent.length
          newContent = fullContent.slice(0, headingIdx) + editContent + '\n' + fullContent.slice(endIdx)
        } else {
          throw new Error('Could not locate section in document')
        }
      }

      const { data: updated, error: putErr } = await supabase
        .from('constitution')
        .update({ content: newContent, updated_at: new Date().toISOString() })
        .eq('is_active', true)
        .select()
        .single()
      if (putErr) throw new Error(putErr.message)
      // Save version
      if (constitution.id) {
        await supabase.from('constitution_versions').insert({
          constitution_id: constitution.id,
          content: constitution.content,
          change_source: 'user_edit',
        })
      }
      setConstitution(updated as unknown as Constitution)
      setEditingSectionId(null)
      setEditContent('')
      setSaveSuccess(true)
      fetchVersions()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleRestore = async (version: ConstitutionVersion) => {
    setRestoring(true)
    try {
      const { data: updated, error: putErr } = await supabase
        .from('constitution')
        .update({ content: version.content, updated_at: new Date().toISOString() })
        .eq('is_active', true)
        .select()
        .single()
      if (putErr) throw new Error('Restore failed')
      setConstitution(updated as unknown as Constitution)
      setEditingSectionId(null)
      fetchVersions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restore')
    } finally {
      setRestoring(false)
    }
  }

  // Search functionality
  const handleSearch = (query: string) => {
    setSearchQuery(query)
    if (!query.trim() || !contentRef.current) {
      clearHighlights()
      setActiveResult(0)
      return
    }
    clearHighlights()
    highlightMatches(query)
  }

  const clearHighlights = () => {
    if (!contentRef.current) return
    const marks = contentRef.current.querySelectorAll('mark[data-search]')
    marks.forEach(m => {
      const parent = m.parentNode
      if (parent) { parent.replaceChild(document.createTextNode(m.textContent || ''), m); parent.normalize() }
    })
  }

  const highlightMatches = (query: string) => {
    if (!contentRef.current) return
    const walker = document.createTreeWalker(contentRef.current, NodeFilter.SHOW_TEXT)
    const lowerQuery = query.toLowerCase()
    const textNodes: Text[] = []
    while (walker.nextNode()) textNodes.push(walker.currentNode as Text)

    let matchCount = 0
    for (const textNode of textNodes) {
      const text = textNode.textContent || ''
      const lowerText = text.toLowerCase()
      let startIdx = 0
      const fragments: (string | { match: string })[] = []
      let hasMatch = false

      while (true) {
        const idx = lowerText.indexOf(lowerQuery, startIdx)
        if (idx === -1) { fragments.push(text.slice(startIdx)); break }
        hasMatch = true
        if (idx > startIdx) fragments.push(text.slice(startIdx, idx))
        fragments.push({ match: text.slice(idx, idx + query.length) })
        startIdx = idx + query.length
      }

      if (hasMatch && textNode.parentNode) {
        const span = document.createElement('span')
        for (const frag of fragments) {
          if (typeof frag === 'string') { span.appendChild(document.createTextNode(frag)) }
          else {
            const mark = document.createElement('mark')
            mark.setAttribute('data-search', 'true')
            mark.style.background = matchCount === 0 ? '#f97316' : '#fbbf24'
            mark.style.color = '#000'
            mark.style.padding = '0 2px'
            mark.style.borderRadius = '2px'
            mark.textContent = frag.match
            span.appendChild(mark)
            matchCount++
          }
        }
        textNode.parentNode.replaceChild(span, textNode)
      }
    }
    if (matchCount > 0) {
      const firstMark = contentRef.current.querySelector('mark[data-search]')
      firstMark?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    setActiveResult(0)
  }

  const jumpToResult = (direction: 'next' | 'prev') => {
    if (!contentRef.current) return
    const marks = contentRef.current.querySelectorAll('mark[data-search]')
    if (marks.length === 0) return
    marks.forEach(m => ((m as HTMLElement).style.background = '#fbbf24'))
    let newIdx = direction === 'next' ? (activeResult + 1) % marks.length : (activeResult - 1 + marks.length) % marks.length
    setActiveResult(newIdx)
    const target = marks[newIdx] as HTMLElement
    target.style.background = '#f97316'
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const getMatchCount = () => contentRef.current?.querySelectorAll('mark[data-search]').length || 0

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gray-400 text-lg">Loading...</div></div>
  if (error && !constitution) return <div className="p-6"><div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-300">{error}</div></div>

  return (
    <div className="p-6 mx-auto" style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">✏️ Edit Constitution</h1>
          {saveSuccess && <span className="px-2 py-0.5 text-xs rounded bg-green-900/50 text-green-300 border border-green-700">✓ Saved</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowVersionHistory(!showVersionHistory)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${showVersionHistory ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
            History {showVersionHistory ? '▴' : '▾'}
          </button>
          <button onClick={() => navigate('/portfolio-manager/constitution')}
            className="px-4 py-1.5 rounded-lg text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
            ← Back
          </button>
        </div>
      </div>

      {/* Persistent search bar — sticky */}
      <div className="sticky top-0 z-10 bg-[var(--color-bg-primary)] pb-3 pt-1 -mx-6 px-6" style={{ backdropFilter: 'blur(8px)' }}>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <input type="text" value={searchQuery} onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search..." className="w-full px-4 py-2 pl-9 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500" />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">🔍</span>
          </div>
          {searchQuery && (
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <span>{getMatchCount() > 0 ? `${activeResult + 1}/${getMatchCount()}` : '0 results'}</span>
              <button onClick={() => jumpToResult('prev')} className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white">↑</button>
              <button onClick={() => jumpToResult('next')} className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white">↓</button>
              <button onClick={() => handleSearch('')} className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white">✕</button>
            </div>
          )}
        </div>
      </div>

      {saveError && <div className="mb-4 bg-red-900/30 border border-red-700 rounded-lg p-3 text-red-300 text-sm">{saveError}</div>}

      <div className="flex gap-4">
        {/* Main content */}
        <div className="flex-1" ref={contentRef}>
          <div className="flex items-center gap-2 mb-3">
            <button onClick={collapseAll} className="px-2 py-1 text-[10px] rounded bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors">
              Collapse All
            </button>
            <button onClick={expandAll} className="px-2 py-1 text-[10px] rounded bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors">
              Expand All
            </button>
            <span className="text-[10px] text-gray-400">Click heading chevron to collapse • Click content to edit</span>
          </div>

          {sections.map((section) => (
            <EditableCollapsibleSection
              key={section.id}
              section={section}
              collapsed={collapsed}
              toggleCollapse={toggleCollapse}
              editingSectionId={editingSectionId}
              editContent={editContent}
              setEditContent={setEditContent}
              onSectionClick={handleSectionClick}
              onSave={handleSave}
              onCancel={handleCancel}
              saving={saving}
              saveError={saveError}
            />
          ))}

          {sections.length === 0 && (
            <div className="text-center text-gray-500 py-12">Constitution is empty. Upload a .md file from the main Constitution page.</div>
          )}
        </div>

        {/* Version History Sidebar */}
        {showVersionHistory && (
          <div className="w-64 shrink-0 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col max-h-[80vh] sticky top-6 shadow-sm">
            <div className="px-4 py-3 border-b border-gray-200 shrink-0">
              <h3 className="text-sm font-semibold text-gray-700">Version History</h3>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-gray-700">
              {versions.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">No versions yet.</div>
              ) : (
                versions.slice(0, 10).map((version) => (
                  <div key={version.id} className="px-4 py-3 hover:bg-gray-700/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">{new Date(version.createdAt).toLocaleString()}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${version.changeSource === 'user_edit' ? 'bg-blue-900/50 text-blue-300' : 'bg-purple-900/50 text-purple-300'}`}>
                        {version.changeSource}
                      </span>
                    </div>
                    <button onClick={() => handleRestore(version)} disabled={restoring}
                      className="mt-1 text-xs text-amber-400 hover:text-amber-300 disabled:opacity-50">
                      {restoring ? '...' : 'Restore'}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Scroll to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 w-10 h-10 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-500 flex items-center justify-center text-lg z-50 transition-colors"
        title="Scroll to top"
      >
        ↑
      </button>
    </div>
  )
}
